import { useQuery } from '@apollo/client'
import { usePrompt } from '@ur-solutions/react-components'
import { useTranslate } from '@ur-solutions/react-hooks'
import type { TableMenuItem } from '@/components_deprecated'
import {
  Table as BaseTable,
  SortableHeader,
  TableMenu,
} from '@/components_deprecated'
import React from 'react'
import { isMobileOnly } from 'react-device-detect'
import { useNavigate } from 'react-router'
import styled from 'styled-components'
import { format } from '@/utils/date-fns'
import { useOnErrorAuto, usePermissions, useUser } from '@/utils/hooks'
import { fromGlobalId } from '@/utils/parsing'
import { PERMISSIONS } from '@/utils/permissions'
import { ALL_FORM_TEMPLATES_QUERY } from '../queries'
import type { FormTableFilter, FormTemplateShallow } from '../types'
import type {
  AllFormTemplatesQuery,
  AllFormTemplatesQueryVariables,
  FormTemplateForFormTable,
  FormTemplateOrdering,
} from '../types.graphql'
import { ResponsePrompt } from './ResponsePrompt'
import { ExportPrompt } from './ExportPrompt'

const Table = styled(BaseTable)`
  tbody tr {
    height: 3.875rem;
  }

  span[role='button'] {
    cursor: pointer;

    &:hover {
      color: ${props => props.theme.colors.secondary};
    }
  }

  i.view-responses-icon {
    display: none;
  }

  tr:hover {
    i.view-responses-icon {
      display: inline;
    }
  }
`

interface FormTableProps {
  filter: FormTableFilter
  onSort: (value: FormTemplateOrdering) => void
}

export const FormTable: React.FC<FormTableProps> = ({ filter, onSort }) => {
  const translations = useTranslate({
    name: 'common.name',
    updatedAt: 'common.updated-at',
    lastResponseAt: 'form.last-response-at',
    used: 'form.table-label-used',

    editThisForm: 'form.edit-this-form',
    fillOutThisForm: 'form.fill-out-this-form',
    noResults: 'common.no-results',
    answers: 'form.see-answers',
    sums: 'common.sums',
    exportResponses: 'form.export-responses',
  })

  const navigate = useNavigate()

  const me = useUser()
  const onErrorAuto = useOnErrorAuto()
  const addPrompt = usePrompt()
  const { hasPermissions } = usePermissions()

  const canEdit = hasPermissions(
    PERMISSIONS.formcreator.change.formtemplate,
    true
  )
  const canViewFilled = hasPermissions(
    PERMISSIONS.formcreator.view.filledoutform,
    true
  )
  const canViewOthers = hasPermissions(
    PERMISSIONS.formcreator.change.filledoutform,
    true
  )

  const { data, loading } = useQuery<
    AllFormTemplatesQuery,
    AllFormTemplatesQueryVariables
  >(ALL_FORM_TEMPLATES_QUERY, {
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    variables: {
      q: filter.query,
      archived: filter.archived,
      orderBy: filter.orderBy,
    },
    onError: onErrorAuto(),
  })

  async function handleOpenResponses(form: FormTemplateShallow) {
    const { data: responses } = await addPrompt<string[] | null>(resolve => (
      <ResponsePrompt
        user={me}
        canViewOthers={canViewOthers}
        form={form}
        onResolve={resolve}
      />
    ))
    if (responses === null || !responses.length) return

    const ids = responses.reduce<number[]>((acc, relayId) => {
      const id = fromGlobalId(relayId)?.[1]
      if (typeof id === 'undefined') return acc
      return [...acc, id]
    }, [])

    if (!ids.length) return

    void navigate(`/formcreator/${form.id}/responses?ids=${ids.join(',')}`)
  }

  async function handleExportResponses(form: FormTemplateForFormTable) {
    await addPrompt<boolean>(resolve => (
      <ExportPrompt formId={form.id} onResolve={resolve} />
    ))
  }

  const forms =
    data?.allFormTemplates.edges
      .map(edge => edge.node)
      ?.filter(el => el.type !== 'MATERIAL') ?? []

  function getFormMenuItems(template: FormTemplateForFormTable) {
    const items: TableMenuItem<FormTemplateForFormTable>[] = []

    const hasDateAndNumberFields =
      template.fields.some(field => field.fieldType === 'DATE') &&
      template.fields.some(field => field.fieldType === 'NUMBER')

    canEdit &&
      items.push({
        label: translations.editThisForm,
        data: template,
        role: 'link',
        onClick: () => void navigate(`/formcreator/edit/${template.id}`),
      })
    items.push({
      label: translations.fillOutThisForm,
      data: template,
      role: 'link',
      onClick: () => void navigate(`/formcreator/${template.id}`),
    })
    canViewFilled &&
      items.push({
        label: translations.answers,
        onClick: () => handleOpenResponses(template),
      })

    canViewOthers &&
      items.push({
        label: translations.exportResponses,
        onClick: () => handleExportResponses(template),
      })

    canViewOthers &&
      hasDateAndNumberFields &&
      items.push({
        label: translations.sums,
        onClick: () => void navigate(`/formcreator/${template.id}/sums`),
      })

    return items
  }

  return (
    <Table
      loading={loading}
      noData={forms.length === 0}
      noDataText={translations.noResults}
    >
      <thead>
        <tr>
          <SortableHeader
            baseValue="name"
            sortValue={filter.orderBy}
            onSort={onSort}
          >
            {translations.name}
          </SortableHeader>

          {!isMobileOnly && (
            <>
              <SortableHeader
                baseValue="updatedAt"
                sortValue={filter.orderBy}
                onSort={onSort}
              >
                {translations.updatedAt}
              </SortableHeader>

              <SortableHeader
                baseValue="lastResponseAt"
                sortValue={filter.orderBy}
                onSort={onSort}
              >
                {translations.lastResponseAt}
              </SortableHeader>
            </>
          )}

          <SortableHeader
            baseValue="numberOfUses"
            sortValue={filter.orderBy}
            onSort={onSort}
          >
            {translations.used}
          </SortableHeader>

          <th></th>
        </tr>
      </thead>

      <tbody>
        {forms?.map(form => {
          const menuItems = getFormMenuItems(form)
          const updated = format(new Date(form.updatedAt), 'dd. MMM yyyy')
          const lastResponse = !form.lastResponseAt
            ? '-'
            : format(new Date(form.lastResponseAt), 'dd. MMM yyyy HH:mm')

          return (
            <tr key={form.id}>
              <td>{form.name}</td>

              {!isMobileOnly && (
                <>
                  <td>{updated}</td>

                  <td>{lastResponse}</td>
                </>
              )}

              <td width="1px" className="center">
                <span>
                  {canViewOthers
                    ? form.filledOutFormsCount
                    : form.myResponsesCount}
                </span>
              </td>

              <TableMenu items={menuItems} loading={loading}></TableMenu>
            </tr>
          )
        })}
      </tbody>
    </Table>
  )
}
